import React, { useCallback, useContext, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import OfficeFormType from '../enums/office-form-types';
import type Room from '../models/room/room';
import * as styles from './modal.styles';
import * as officeStyles from '../offices.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import DeleteRoomModal from './delete-room-modal.component';
import StyleConstants from '../../../core/styles.constants';
import AppConstants from '../../../core/app.constants';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly initialRoom: Room;
  readonly isRoomEmptyOrInUse: (room: Room) => boolean;
  readonly handleSave: (value: Room) => void;
  /**
   * `acknowledged` says the caller was shown what the deletion cancels and went ahead. The server
   * refuses the deletion without it whenever there is anything to cancel (5.3.1).
   */
  readonly onDelete: (value: Room, acknowledged: boolean) => void;
  readonly canEditCapacity: boolean;
  readonly floorplanEnabled: boolean;
}

const EditRoomModal = ({
  showModal,
  handleClose,
  initialRoom,
  handleSave,
  onDelete,
  isRoomEmptyOrInUse,
  canEditCapacity,
  floorplanEnabled,
}: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [room, setRoom] = useState<Room>(initialRoom);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const cancelUpdateRoom = (): void => {
    setRoom(initialRoom);
    handleClose();
  };

  const ShouldCloseModal = (): void => {
    if (!isRoomEmptyOrInUse(room)) {
      // initialRoom = room;
      handleSave(room);
      handleClose();
      return;
    }
    setRoom(initialRoom);
  };

  const handleDelete = (acknowledged: boolean): void => {
    onDelete(room, acknowledged);
    handleClose();
  };

  const handleRoomChange = useCallback(
    (officeFormType: OfficeFormType, value: unknown) => {
      const roomCopy = { ...room };
      switch (officeFormType) {
        case OfficeFormType.name:
          roomCopy.name = value as string;
          break;
        case OfficeFormType.capacity:
          roomCopy.capacity = value as number;
          break;
        case OfficeFormType.reservableThroughFloorplanEnabled:
          roomCopy.isReservableThroughFloorPlan = value as boolean;
          break;
        case OfficeFormType.roomThresholdMinPercentage:
          roomCopy.thresholdMinPercentage = value as number | undefined;
          break;
        case OfficeFormType.roomThresholdMaxPercentage:
          roomCopy.thresholdMaxPercentage = value as number | undefined;
          break;
        default:
          break;
      }
      setRoom(roomCopy);
    },
    [setRoom, room],
  );

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>{t('edit-room')}</span>
        <LabeledInput
          labelText={t('room-name').toString()}
          value={room.name}
          onChange={(value): void => {
            handleRoomChange(OfficeFormType.name, value);
          }}
        />
        <LabeledInput
          tooltipText={t('max-capacity-info-message').toString()}
          tooltipId={AppConstants.TOOLTIP_GENERAL_ID}
          labelText={t('max-capacity').toString()}
          value={room.capacity.toString()}
          // In a floorplan office the desks drawn in a room are its seats — unless it is booked by
          // capacity instead of through the plan, in which case there are no desks to count.
          disabled={!canEditCapacity && room.isReservableThroughFloorPlan}
          onChange={(value): void => {
            handleRoomChange(OfficeFormType.capacity, Number(value));
          }}
        />
        <Row itemSpacing={12}>
          <LabeledInput
            tooltipText={t('threshold-min-info-message').toString()}
            tooltipId={AppConstants.TOOLTIP_GENERAL_ID}
            labelText={t('threshold-min').toString()}
            type="number"
            min={0}
            max={100}
            value={room.thresholdMinPercentage?.toString() ?? ''}
            onChange={(value): void => {
              handleRoomChange(OfficeFormType.roomThresholdMinPercentage, value === '' ? undefined : Number(value));
            }}
          />
          <LabeledInput
            tooltipText={t('threshold-max-info-message').toString()}
            tooltipId={AppConstants.TOOLTIP_GENERAL_ID}
            labelText={t('threshold-max').toString()}
            type="number"
            min={0}
            max={100}
            value={room.thresholdMaxPercentage?.toString() ?? ''}
            onChange={(value): void => {
              handleRoomChange(OfficeFormType.roomThresholdMaxPercentage, value === '' ? undefined : Number(value));
            }}
          />
        </Row>
        {floorplanEnabled ? (
          <Row className={style(officeStyles.checkbox)} itemSpacing={8} crossAxisAlignment={CrossAxisAlignment.Center}>
            <Switch
              checked={room.isReservableThroughFloorPlan}
              onChange={(value): void => {
                handleRoomChange(OfficeFormType.reservableThroughFloorplanEnabled, value);
              }}
              onColor={StyleConstants.ORANGE_COLOR}
              onHandleColor={StyleConstants.WHITE_COLOR}
              handleDiameter={styles.switchDiameter}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow={styles.switchBoxShadow}
              activeBoxShadow={styles.switchActiveBoxShadow}
              height={styles.switchHeight}
              width={styles.switchWidth}
            />
            <span>{t('reservable-through-floorplan')}</span>
          </Row>
        ) : null}

        {/* Offered in a floorplan office too. Capacity there follows the plan, but a room that is
            gone has to be removable: the deletion takes its outline and desks off the floorplan,
            and the dialog says so before anything happens. */}
        {room.id ? (
          <Row
            className={style(styles.deleteButton)}
            crossAxisAlignment={CrossAxisAlignment.Center}
            onClick={(): void => {
              setShowDeleteModal(true);
            }}
          >
            <span>{t('delete-room')}</span>
            <DeleteIcon className={style(styles.deleteIcon)} />
          </Row>
        ) : null}

        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              onClick={(): void => {
                ShouldCloseModal();
              }}
            >
              {t('edit')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={cancelUpdateRoom}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
      <DeleteRoomModal
        showModal={showDeleteModal}
        roomId={room.id}
        roomName={room.name}
        handleAccept={handleDelete}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
    </Modal>
  );
};

export default EditRoomModal;
